import React, { useEffect, useState } from "react";
import { Key, Eye, EyeOff, Save, Trash2, CheckCircle2, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";

/**
 * <AISecretsCard /> — Admin UI for runtime LLM keys.
 *
 * Lives inside the Settings tab. The backend NEVER returns a cleartext value
 * (only status / source / last4) — when admins update a key it ships in the
 * PUT body and the form clears.
 */
const AISecretsCard = () => {
    const [secrets, setSecrets] = useState([]);
    const [loading, setLoading] = useState(true);
    const [draft, setDraft] = useState({}); // {key: cleartextValue}
    const [reveal, setReveal] = useState({}); // {key: bool}
    const [busy, setBusy] = useState({}); // {key: actionName}

    const load = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/admin/app-secrets");
            setSecrets(data || []);
        } catch (e) {
            if (e?.response?.status === 403) {
                // SOC 2 CC7.4 — log the denied attempt in the tamper-evident audit log.
                api.post("/admin/app-secrets/audit/denied").catch(() => {});
            }
            setSecrets([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const save = async (key) => {
        const value = (draft[key] || "").trim();
        if (!value) { toast.error("Paste a value first"); return; }
        setBusy((b) => ({ ...b, [key]: "saving" }));
        try {
            await api.put(`/admin/app-secrets/${key}`, { value });
            setDraft((d) => ({ ...d, [key]: "" }));
            await load();
            toast.success("Key saved");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Save failed");
        } finally {
            setBusy((b) => ({ ...b, [key]: null }));
        }
    };

    const clear = async (key) => {
        if (!window.confirm(`Clear the saved ${key}? The app will fall back to the environment variable if one is set.`)) return;
        setBusy((b) => ({ ...b, [key]: "clearing" }));
        try {
            await api.delete(`/admin/app-secrets/${key}`);
            await load();
            toast.success("Key cleared");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Clear failed");
        } finally {
            setBusy((b) => ({ ...b, [key]: null }));
        }
    };

    const test = async (key) => {
        const candidate = (draft[key] || "").trim();
        setBusy((b) => ({ ...b, [key]: "testing" }));
        try {
            const { data } = await api.post(`/admin/app-secrets/${key}/test`, { value: candidate || undefined });
            toast.success(`✓ ${data.reply || "OK"}`);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Test failed");
        } finally {
            setBusy((b) => ({ ...b, [key]: null }));
        }
    };

    return (
        <div className="bg-white border border-black/5 rounded-2xl p-8" data-testid="ai-secrets-card">
            <div className="flex items-start gap-4 mb-6">
                <div className="w-12 h-12 rounded-full bg-[#F2D8D5] flex items-center justify-center flex-shrink-0">
                    <Key className="w-6 h-6 text-[#E53935]" strokeWidth={1.5} />
                </div>
                <div className="flex-1 min-w-0">
                    <h3 className="font-heading text-xl text-[#1C252E] font-medium mb-1">AI &amp; integrations keys</h3>
                    <p className="text-sm text-[#4A5568] leading-relaxed">
                        Paste API keys here to enable AI features and file uploads without redeploying.
                        Saved keys take precedence over <code className="text-xs bg-[#F4F1EA] px-1.5 py-0.5 rounded">.env</code> file values.
                        For security, saved keys are never displayed in cleartext — only the last 4 characters appear.
                    </p>
                </div>
            </div>

            {loading ? (
                <p className="text-sm text-[#4A5568]">Loading…</p>
            ) : (
                <ul className="space-y-5" data-testid="ai-secrets-list">
                    {secrets.map((s) => {
                        const action = busy[s.key];
                        const isRevealed = !!reveal[s.key];
                        return (
                            <li key={s.key} className="border border-black/5 rounded-2xl p-5" data-testid={`ai-secret-row-${s.key}`}>
                                <div className="flex items-baseline justify-between gap-3 flex-wrap">
                                    <div>
                                        <h4 className="font-bold text-[#1C252E]">{s.label}</h4>
                                        <p className="text-xs text-[#4A5568] mt-0.5 max-w-prose">{s.description}</p>
                                    </div>
                                    <SecretStatusBadge status={s.status} source={s.source} last4={s.last4} />
                                </div>

                                <div className="mt-4 flex flex-wrap gap-2 items-stretch">
                                    <div className="relative flex-1 min-w-[240px]">
                                        <input
                                            type={isRevealed ? "text" : "password"}
                                            value={draft[s.key] || ""}
                                            onChange={(e) => setDraft((d) => ({ ...d, [s.key]: e.target.value }))}
                                            placeholder={s.status === "set" ? "Paste a NEW value to replace…" : "Paste your key…"}
                                            data-testid={`ai-secret-input-${s.key}`}
                                            className="w-full rounded-xl border border-black/10 px-3 py-2 pr-10 bg-white text-sm font-mono"
                                            spellCheck={false}
                                            autoComplete="off"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => setReveal((r) => ({ ...r, [s.key]: !isRevealed }))}
                                            aria-label={isRevealed ? "Hide" : "Reveal"}
                                            data-testid={`ai-secret-reveal-${s.key}`}
                                            className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-[#4A5568] hover:text-[#1C252E]"
                                        >
                                            {isRevealed ? <EyeOff className="w-4 h-4" strokeWidth={1.5} /> : <Eye className="w-4 h-4" strokeWidth={1.5} />}
                                        </button>
                                    </div>
                                    <button
                                        onClick={() => save(s.key)}
                                        disabled={action === "saving" || !(draft[s.key] || "").trim()}
                                        data-testid={`ai-secret-save-${s.key}`}
                                        className="inline-flex items-center gap-1 bg-[#E53935] hover:bg-[#D32F2F] text-white rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-60"
                                    >
                                        {action === "saving" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Save className="w-4 h-4" strokeWidth={2} />}
                                        Save
                                    </button>
                                    <button
                                        onClick={() => test(s.key)}
                                        disabled={action === "testing"}
                                        data-testid={`ai-secret-test-${s.key}`}
                                        className="inline-flex items-center gap-1 bg-white border border-black/10 hover:bg-[#F4F1EA] text-[#1C252E] rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-60"
                                        title="Make a tiny test call with this key (uses the draft value if you typed one, otherwise the saved value)"
                                    >
                                        {action === "testing" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <CheckCircle2 className="w-4 h-4" strokeWidth={2} />}
                                        Test
                                    </button>
                                    {s.status === "set" && s.source === "db" && (
                                        <button
                                            onClick={() => clear(s.key)}
                                            disabled={action === "clearing"}
                                            data-testid={`ai-secret-clear-${s.key}`}
                                            className="inline-flex items-center gap-1 bg-white border border-red-200 hover:bg-red-50 text-red-700 rounded-xl px-4 py-2 text-sm font-medium disabled:opacity-60"
                                        >
                                            {action === "clearing" ? <Loader2 className="w-4 h-4 animate-spin" strokeWidth={2} /> : <Trash2 className="w-4 h-4" strokeWidth={2} />}
                                            Clear
                                        </button>
                                    )}
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

const SecretStatusBadge = ({ status, source, last4 }) => {
    if (status === "set") {
        return (
            <span data-testid="ai-secret-status-set" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold bg-green-100 text-green-900 rounded-full px-2 py-0.5">
                <CheckCircle2 className="w-3 h-3" strokeWidth={2} /> Set via {source || "—"} · ends in ····{last4}
            </span>
        );
    }
    return (
        <span data-testid="ai-secret-status-not-set" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.14em] font-bold bg-amber-100 text-amber-900 rounded-full px-2 py-0.5">
            <AlertTriangle className="w-3 h-3" strokeWidth={2} /> Not set
        </span>
    );
};

export default AISecretsCard;
